import { isDefined } from '@/utils/general'
/**
 * useRebillState Hook
 *
 * This module handles the complex state management for the invoice rebilling process.
 * Rebilling allows users to adjust line items, add new items, and remove items from
 * an existing invoice, generating both a credit for the original invoice and a new
 * rebill invoice with the updated information.
 *
 * Key concepts in rebilling:
 * 1. Original Invoice: The starting invoice that needs adjustment
 * 2. Credit Invoice: Automatically generated to offset the original invoice
 * 3. Rebill Invoice: The new invoice with corrected/updated information
 * 4. Impact Types: How each line item is affected ('impacted', 'added', 'removed')
 * 5. Deltas: The differences in amounts between original and rebill invoices
 *
 * The hook maintains all state needed for this multi-step process and handles
 * complex calculations for tracking financial impacts of changes.
 */

import { useApi } from '@gravitate-js/excalibrr'
import { useQueryClient } from '@tanstack/react-query'
import { message } from 'antd'
import { useMemo, useState } from 'react'
import { useInvoices } from '../../../../../api/useFreight/useInvoices'
import type {
  CreditRebillResponse,
  ImpactedTransactionRequest,
  Invoice,
  InvoiceDetail,
  InvoiceGroup,
  InvoiceTransaction,
  RebillSaveBody,
} from '../../../../../api/useFreight/types'
import { getAPIURL } from '../../../../../utils/api'
import { FreightLineItemForm } from '@/components/Freight/FreightLineItems/components/AddFreightLineItem/types'
import { getOverrideValue, payloadHasOverrides, setRemovedTotalsToZero } from '../utils/calculations'
import { get } from 'lodash'

/**
 * Recalculates transaction-level metadata based on the details within the transaction
 *
 * This function aggregates changes from each detail in a transaction to calculate
 * the overall financial impact at the transaction level. It processes four types of changes:
 *
 * - "impacted": Details that exist in the original but have been modified
 * - "added": New details that didn't exist in the original invoice
 * - "removed": Details that have been removed or zeroed out
 * - null/undefined: Details that have not been changed (or changes were reverted)
 *
 * For each type, we apply different calculations to determine the financial impact:
 * - For impacted details: Use the pre-calculated deltas from each detail
 * - For added details: Add the full amount as a positive delta
 * - For removed details: Subtract the full amount as a negative delta
 * - For unchanged details: No impact on deltas
 *
 * @param recalculatedTransaction - The transaction containing modified details
 * @returns The aggregate metadata with calculated deltas for the entire transaction
 */
const recalculateTransactionMetadata = (recalculatedTransaction: InvoiceTransaction) => {
  // Initialize metadata with zero values
  const metadata = {
    delta_amount: 0,
    delta_accessorial_total: 0,
    delta_base_total: 0,
    delta_surcharge_total: 0,
    delta_total: 0,
    impact_type: 'impacted', // Default impact type
  }

  // Count different types of changes
  let impactedCount = 0
  let addedCount = 0
  let removedCount = 0

  // Process each detail to accumulate the financial impact
  for (const detail of recalculatedTransaction.details) {
    const impactType = detail.credit_rebill_metadata?.impact_type

    // Case 1: Modified existing details
    if (impactType === 'impacted') {
      impactedCount++
      // Add the pre-calculated deltas from the detail's metadata
      metadata.delta_amount += detail.credit_rebill_metadata?.delta_amount || 0
      metadata.delta_accessorial_total += detail.credit_rebill_metadata?.delta_accessorial_total || 0
      metadata.delta_base_total += detail.credit_rebill_metadata?.delta_base_total || 0
      metadata.delta_surcharge_total += detail.credit_rebill_metadata?.delta_surcharge_total || 0
      metadata.delta_total += detail.credit_rebill_metadata?.delta_total || 0
    }

    // Case 2: Newly added details
    else if (impactType === 'added') {
      addedCount++
      // Use override value if available, otherwise use original total
      const detailAmount = detail.total_override || detail.total

      // Add the full amount as a positive delta
      metadata.delta_amount += detail.amount || 0

      // Categorize the amount based on the detail type
      if (detail.type === 'Accessorial') {
        metadata.delta_accessorial_total += detailAmount
      } else if (detail.type === 'Surcharge') {
        metadata.delta_surcharge_total += detailAmount
      } else if (detail.type === 'Base Freight' || detail.type === 'Point to Point') {
        metadata.delta_base_total += detailAmount
      }

      // Add to the total delta
      metadata.delta_total += detailAmount
    }

    // Case 3: Removed details
    else if (impactType === 'removed') {
      removedCount++
      // Use original amount for removed items
      const detailAmount = detail.total_original || detail.total

      // Subtract the full amount as a negative delta
      metadata.delta_amount -= detail.amount || 0

      // Categorize the negative amount based on the detail type
      if (detail.type === 'Accessorial') {
        metadata.delta_accessorial_total -= detailAmount
      } else if (detail.type === 'Surcharge') {
        metadata.delta_surcharge_total -= detailAmount
      } else if (detail.type === 'Base Freight' || detail.type === 'Point to Point') {
        metadata.delta_base_total -= detailAmount
      }

      // Subtract from the total delta
      metadata.delta_total -= detailAmount
    }
    // Case 4: Unchanged details - no impact on deltas
  }

  // Determine overall impact type for the transaction
  if (impactedCount === 0 && addedCount === 0 && removedCount === 0) {
    // No changes to any details
    metadata.impact_type = 'unchanged'
  } else if (impactedCount > 0 && addedCount === 0 && removedCount === 0) {
    // Only modifications
    metadata.impact_type = 'impacted'
  } else if (impactedCount === 0 && addedCount > 0 && removedCount === 0) {
    // Only additions
    metadata.impact_type = 'added'
  } else if (impactedCount === 0 && addedCount === 0 && removedCount > 0) {
    // Only removals
    metadata.impact_type = 'removed'
  } else {
    // Mixed changes
    metadata.impact_type = 'impacted'
  }

  return metadata
}
/**
 * The main rebill state management hook
 *
 * This hook orchestrates the entire rebill process, including:
 * - Triggering the rebill workflow
 * - Managing the state of original, credit, and rebill invoices
 * - Handling overrides for line items (modifications)
 * - Managing additions and removals of line items
 * - Calculating financial impacts of all changes
 * - Saving the finalized rebill
 *
 * @param setSelectedInvoice - Function to update the currently selected invoice in the UI
 * @param selectedInvoice - The currently selected invoice
 * @param invoiceGroupDetails - Details about the invoice group containing all related invoices
 * @returns A collection of state variables and functions for managing the rebill process
 */
export const useRebillState = ({
  setSelectedInvoice, // Function to update the currently selected invoice in parent component
  selectedInvoice, // Currently displayed invoice
  invoiceGroupDetails, // Details of the invoice group this invoice belongs to
}: {
  setSelectedInvoice: (invoice: Invoice | null) => void
  selectedInvoice: Invoice | null
  invoiceGroupDetails: InvoiceGroup | undefined
}) => {
  // UI state for modals and loading indicators
  const [isRecalcModalVisible, setIsRecalcModalVisible] = useState(false)
  const [isConfirmRebillModalVisible, setIsConfirmRebillModalVisible] = useState(false)
  const [isTriggeringCR, setIsTriggeringCR] = useState(false)
  const [isSavingRebill, setIsSavingRebill] = useState(false)
  const [hasProcessedRebillParam, setHasProcessedRebillParam] = useState(false)

  // Core rebill state
  const [isRebillMode, setIsRebillMode] = useState(false)
  const [rebillInvoice, setRebillInvoice] = useState<Invoice | null>(null)
  const [originalInvoice, setOriginalInvoice] = useState<Invoice | null>(null)
  const [rebillInvoiceChain, setRebillInvoiceChain] = useState<Invoice[]>([])

  // API and data access setup
  const api = useApi({ baseURLOverride: getAPIURL() })
  const queryClient = useQueryClient()

  // Mutations for saving and updating rebill data
  const { useSaveRebillInvoiceMutation, useImpactedTransactionMutation } = useInvoices()
  const { mutateAsync: saveRebillInvoice } = useSaveRebillInvoiceMutation()
  const { mutateAsync: updateImpactedTransactions } = useImpactedTransactionMutation()

  /**
   * Calculate and memoize aggregate metadata for the entire rebill
   *
   * This useMemo calculates:
   * 1. Counts: Number of orders, impacted items, added items, and removed items
   * 2. Financial impacts: Total delta and broken down by category (base, accessorial, surcharge)
   *
   * These values are used in the UI to show the overall impact of the rebill changes
   * and to submit the final rebill to the backend.
   */
  const rebillMetadata = useMemo(() => {
    // Initialize with empty values
    const metadata = {
      // Counts
      order_count: 0,
      impacted_line_items: 0,
      added_line_items: 0,
      removed_line_items: 0,

      // Financial impacts
      rebill_diff: 0, // Total financial impact
      rebill_base_diff: 0, // Impact on base freight
      rebill_accessorial_diff: 0, // Impact on accessorials
      rebill_surcharge_diff: 0, // Impact on surcharges
    }

    // Return empty metadata if no rebill invoice exists
    if (!rebillInvoice) return metadata

    // Aggregate data from all transactions
    for (const transaction of rebillInvoice.transactions) {
      // Count orders (each transaction is an order)
      metadata.order_count += 1

      // Count different types of line items
      metadata.impacted_line_items += transaction.details.filter(
        (detail) => detail.credit_rebill_metadata?.impact_type === 'impacted'
      ).length
      metadata.added_line_items += transaction.details.filter(
        (detail) => detail.credit_rebill_metadata?.impact_type === 'added'
      ).length
      metadata.removed_line_items += transaction.details.filter(
        (detail) => detail.credit_rebill_metadata?.impact_type === 'removed'
      ).length

      if (transaction.credit_rebill_metadata) {
        // Sum financial impacts across all transactions
        metadata.rebill_diff += transaction.credit_rebill_metadata.delta_total || 0
        metadata.rebill_base_diff += transaction.credit_rebill_metadata.delta_base_total || 0
        metadata.rebill_accessorial_diff += transaction.credit_rebill_metadata.delta_accessorial_total || 0
        metadata.rebill_surcharge_diff += transaction.credit_rebill_metadata.delta_surcharge_total || 0
      }
    }

    return metadata
  }, [rebillInvoice])

  /**
   * Calculate and update metadata for a detail when its values are overridden
   *
   * This function:
   * 1. Creates or updates the metadata for a line item when it's changed
   * 2. Calculates financial deltas between original and new values
   * 3. Sets the appropriate impact type ('impacted' or 'removed')
   * 4. Categorizes the financial impact based on line item type
   *
   * @param rebillDetail - The invoice detail being modified
   * @param totalOverride - The new total value
   * @param amountOverride - The new quantity/amount value
   * @returns Updated metadata with all calculated deltas
   */
  const fixMetadataOnDetail = (
    rebillDetail: InvoiceDetail,
    totalOverride: number,
    amountOverride: number,
    hasOverride: boolean
  ) => {
    // Create metadata object if it doesn't exist or use existing one
    const metadataToFix = rebillDetail.credit_rebill_metadata || {
      impact_type: null,
      delta_total: 0,
      delta_amount: 0,
      delta_base_total: 0,
      delta_surcharge_total: 0,
      delta_accessorial_total: 0,
    }

    // Handle amount override (quantity changes)
    if (amountOverride) {
      metadataToFix.delta_amount = amountOverride - rebillDetail.amount
    }

    // Calculate delta from original value, not from previous override
    // This ensures we're always measuring change from original invoice
    const originalTotal = rebillDetail.total_original || rebillDetail.total
    metadataToFix.delta_total = totalOverride - originalTotal

    // Categorize the financial impact based on line item type
    if (rebillDetail.type === 'Accessorial') {
      metadataToFix.delta_accessorial_total = totalOverride - originalTotal
    }
    if (rebillDetail.type === 'Surcharge') {
      metadataToFix.delta_surcharge_total = totalOverride - originalTotal
    }
    if (rebillDetail.type === 'Base Freight' || rebillDetail.type === 'Point to Point') {
      metadataToFix.delta_base_total = totalOverride - originalTotal
    }

    // Determine impact type based on amount and existing state
    if (totalOverride === 0) {
      // If total is zero, it's considered removed
      metadataToFix.impact_type = 'removed'
    } else if (!rebillDetail.total_original && rebillDetail.id.startsWith('dummy-')) {
      // If this is a newly added item (has dummy ID and no original total)
      metadataToFix.impact_type = 'added'
    } else if (!hasOverride) {
      metadataToFix.impact_type = null
    } else {
      // Otherwise it's an existing item that was modified
      metadataToFix.impact_type = 'impacted'
    }
    return metadataToFix
  }

  /**
   * Override an existing line item in the rebill invoice
   *
   * This function:
   * 1. Finds the transaction containing the detail to override
   * 2. Updates the detail with new values and recalculates metadata
   * 3. Sends the update to the backend for validation and any dependent changes
   * 4. Updates the local state with the result
   *
   * @param detail - The invoice detail to override
   * @param amountOverride - The new quantity/amount value
   * @param totalOverride - The new total value
   */
  const overrideRebillLineItem = async (
    detail: InvoiceDetail,
    productGroupOverride: string | null,
    totalOverride: number,
    values: FreightLineItemForm
  ) => {
    // Find the transaction containing this detail
    const transaction = rebillInvoice?.transactions.find((t) => t.details.some((d) => d.id === detail.id))

    // Exit if the detail doesn't belong to any transaction
    if (!transaction) return

    let overrideToSave = totalOverride
    // If amount is overridden, recalculate the total based on rate
    const amountOverride = values.amount
    const rateOverride = values.rate ?? detail.rate
    if (isDefined(amountOverride)) {
      overrideToSave = amountOverride * rateOverride
    }

    // Create updated transaction with the override
    const updatedTransaction = {
      ...transaction,
      details: transaction.details.map((rebillDetail) => {
        if (rebillDetail.id === detail.id) {
          // Return updated detail with new values and metadata
          const updatedPayload = {
            ...rebillDetail,
            product_group: values.product_group || rebillDetail.product_group,
            product_group_override: getOverrideValue(productGroupOverride, rebillDetail, 'product_group'),
            product_id: values.product?.value || rebillDetail.product_id,
            product_id_override: getOverrideValue(values.product?.value, rebillDetail, 'product_id'),
            product_name: values.product?.label || rebillDetail.product_name,
            product_name_override: getOverrideValue(values.product?.label, rebillDetail, 'product_name'),
            uom: values.uom || rebillDetail.uom,
            uom_override: getOverrideValue(values.uom, rebillDetail, 'uom'),
            subtype: values.base_freight_subtype || rebillDetail.subtype,
            subtype_override: getOverrideValue(values.base_freight_subtype, rebillDetail, 'subtype'),
            origin_id: values.origin?.value || rebillDetail.origin_id,
            origin_id_override: getOverrideValue(values.origin?.value, rebillDetail, 'origin_id'),
            origin_name: values.origin?.label || rebillDetail.origin_name,
            origin_name_override: getOverrideValue(values.origin?.label, rebillDetail, 'origin_name'),
            destination_id: values.destination?.value || rebillDetail.destination_id,
            destination_id_override: getOverrideValue(values.destination?.value, rebillDetail, 'destination_id'),
            destination_name: values.destination?.label || rebillDetail.destination_name,
            destination_name_override: getOverrideValue(values.destination?.label, rebillDetail, 'destination_name'),
            bol_number: values.bol_number || rebillDetail.bol_number,
            bol_number_override: getOverrideValue(values.bol_number, rebillDetail, 'bol_number'),
            amount: amountOverride ?? rebillDetail.amount,
            amount_override: getOverrideValue(amountOverride, rebillDetail, 'amount'),
            rate: rateOverride,
            rate_override: getOverrideValue(rateOverride, rebillDetail, 'rate'),
            total: overrideToSave,
            total_override: getOverrideValue(overrideToSave, rebillDetail, 'total'),
            override_by: null, // Clear any previous override info
          }
          const hasOverride = payloadHasOverrides(updatedPayload)
          // Calculate the metadata for this change
          const fixedMetadata = fixMetadataOnDetail(updatedPayload, overrideToSave, amountOverride, hasOverride)
          return { ...updatedPayload, credit_rebill_metadata: fixedMetadata, has_override: hasOverride }
        }
        return rebillDetail
      }),
    }

    if (!originalInvoice || !invoiceGroupDetails) {
      message.error('Original invoice not found')
      return
    }
    // Prepare request for backend API
    const request: ImpactedTransactionRequest = {
      invoice_group_id: invoiceGroupDetails?.id,
      invoice_number: originalInvoice.invoice_number,
      ...updatedTransaction,
    }

    fixImpactedTransaction(request, updatedTransaction)
  }

  /**
   * Process an updated transaction with the backend and update local state
   *
   * This function:
   * 1. Sends the updated transaction to the backend for processing
   * 2. Receives back potentially modified details (backend may add/modify details)
   * 3. Merges the backend response with our local state
   * 4. Recalculates all metadata after the changes
   * 5. Updates the rebill invoice state with the new transaction
   *
   * @param request - The request object to send to the backend
   * @param updatedTransaction - The locally updated transaction
   */
  const fixImpactedTransaction = async (
    request: ImpactedTransactionRequest,
    updatedTransaction: InvoiceTransaction
  ) => {
    // Send to backend and get updated transaction details
    const impactedTransactionDetails = await updateImpactedTransactions(request)

    // Start with the transaction we updated locally
    const recalculatedTransaction = {
      ...updatedTransaction,
      details: [
        // Update any existing details that the backend may have modified
        ...updatedTransaction.details.map((detail) => {
          // Look for this detail in the backend response
          const impactedDetail = impactedTransactionDetails.find((t) => t.id === detail.id)
          // If found in response, use the backend version, otherwise keep our local version
          if (impactedDetail) {
            return impactedDetail
          }
          return detail
        }),
      ],
    }

    // Add any new details that the backend might have added
    for (const detail of impactedTransactionDetails) {
      // If this detail doesn't exist in our recalculated transaction, add it
      if (!recalculatedTransaction.details.find((d) => d.id === detail.id)) {
        recalculatedTransaction.details.push(detail)
      }
    }

    // Recalculate the transaction-level metadata after all detail changes
    recalculatedTransaction.credit_rebill_metadata = recalculateTransactionMetadata(recalculatedTransaction)

    // Get the current transactions (or empty array if rebillInvoice is null)
    const oldTransactions = rebillInvoice ? rebillInvoice.transactions : []

    // Create a new invoice with the updated transaction
    const newRebillInvoice = {
      ...rebillInvoice,
      transactions: [
        ...oldTransactions.map((t) => {
          // Replace the transaction that was updated, keep others unchanged
          return t.order_id === updatedTransaction.order_id ? recalculatedTransaction : t
        }),
      ],
    }

    // Update both the rebill invoice state and the selected invoice in the UI
    setRebillInvoice(newRebillInvoice as Invoice)
    setSelectedInvoice(newRebillInvoice as Invoice)
  }

  /**
   * Add a new line item to a transaction in the rebill invoice
   *
   * This function:
   * 1. Finds the transaction to add the detail to
   * 2. Adds the new detail to the transaction
   * 3. Recalculates metadata for the transaction
   * 4. Sends the updated transaction to the backend
   * 5. Updates the local state with the result
   *
   * @param detail - The new invoice detail to add
   * @param orderId - The ID of the order/transaction to add the detail to
   */
  const addRebillLineItem = async (detail: InvoiceDetail, orderId: string) => {
    if (!rebillInvoice || !invoiceGroupDetails) return
    // Find the transaction to add the detail to
    const transaction = rebillInvoice.transactions.find((t) => t.order_id === orderId)
    if (!transaction) return

    // Ensure the new detail has proper metadata for an added item
    const detailWithMetadata = {
      ...detail,
      // Ensure we have credit_rebill_metadata with impact_type set to "added"
      credit_rebill_metadata: {
        ...detail.credit_rebill_metadata,
        impact_type: 'added',
        delta_total: detail.total,
        delta_amount: detail.amount,
        // Set the appropriate category delta based on the detail type
        delta_base_total: detail.type === 'Base Freight' || detail.type === 'Point to Point' ? detail.total : 0,
        delta_accessorial_total: detail.type === 'Accessorial' ? detail.total : 0,
        delta_surcharge_total: detail.type === 'Surcharge' ? detail.total : 0,
      },
    }

    // Create updated transaction with the new detail
    const updatedTransaction = {
      ...transaction,
      details: [...transaction.details, detailWithMetadata], // Add the new detail with metadata
    }

    // Prepare request for the backend API
    const fixedTransaction: ImpactedTransactionRequest = {
      ...updatedTransaction,
      invoice_group_id: invoiceGroupDetails?.id,
      invoice_number: rebillInvoice.invoice_number,
      // Calculate new metadata with the added detail
      credit_rebill_metadata: recalculateTransactionMetadata(updatedTransaction),
    }

    // Send to backend and update state
    fixImpactedTransaction(fixedTransaction, updatedTransaction)
  }

  /**
   * Initiate the rebill process for the current invoice
   *
   * This function:
   * 1. Shows a loading indicator
   * 2. Requests the backend to prepare a rebill package
   * 3. Gets back three invoices: rebill, credit, and original
   * 4. Updates state with these invoices
   * 5. Activates rebill mode in the UI
   *
   * The backend creates a credit invoice (to cancel out the original)
   * and a rebill invoice (the new corrected invoice) which the user
   * can then edit.
   */
  const triggerRebillMode = async () => {
    // Show loading modal
    setIsRecalcModalVisible(true)

    // Request rebill creation from backend
    const response: CreditRebillResponse = await api.post<CreditRebillResponse>('freight/invoice/trigger_rebill', {
      //@ts-ignore
      invoice_number: selectedInvoice?.invoice_number,
      invoice_group_id: invoiceGroupDetails?.id,
    })

    if (response) {
      const rebillInvoice = setRemovedTotalsToZero(response.rebill_invoice)
      // Collect all three invoices in a chain
      const invoices = [
        rebillInvoice, // The new invoice we'll modify
        response.credit_invoice, // Credit to offset original
        response.original_invoice, // The original invoice being rebilled
      ]

      // Update state with all three invoices
      setRebillInvoiceChain(invoices)
      setOriginalInvoice(response.original_invoice)

      // Select the rebill invoice to display in the UI
      setSelectedInvoice(invoices[0])
      setRebillInvoice(rebillInvoice)

      // Activate rebill mode in the UI
      setIsRebillMode(true)
    }

    // Hide loading indicators
    setIsRecalcModalVisible(false)
    setIsTriggeringCR(false)
  }
  /**
   * Save the finalized rebill invoice
   *
   * This function:
   * 1. Prepares all transactions for saving
   * 2. Formats temporary IDs and manual flags
   * 3. Compiles financial impact metadata
   * 4. Sends the complete rebill package to the backend
   * 5. Resets the UI state after successful save
   * 6. Invalidates queries to refresh data
   */
  const handleSaveRebill = async () => {
    if (!rebillInvoice || !originalInvoice || !invoiceGroupDetails) return
    // Show saving indicator
    setIsSavingRebill(true)

    // Process all transactions for saving
    const transactionsToSave = rebillInvoice.transactions.map((transaction) => {
      return {
        ...transaction,
        details: transaction.details.map((detail) => {
          detail.product_group = detail.product_group_original
          detail.product_id = detail.product_id_original
          detail.product_name = detail.product_name_original
          detail.uom = detail.uom_original
          detail.subtype = detail.subtype_original
          detail.origin_id = detail.origin_id_original
          detail.origin_name = detail.origin_name_original
          detail.destination_id = detail.destination_id_original
          detail.destination_name = detail.destination_name_original
          detail.bol_number = detail.bol_number_original
          detail.amount = detail.amount_original
          detail.rate = detail.rate_original
          detail.total = detail.total_original

          // Handle temporary IDs for newly added details
          if (detail.id.startsWith('dummy-')) {
            return {
              ...detail,
              id: null, // Backend will assign a real ID
              manual: true, // Mark as manually added
            }
          }

          // For existing details, ensure manual flag is set if applicable
          return { ...detail, manual: detail.manual || false }
        }),
      }
    })

    // Prepare the complete rebill save request
    const rebillSaveBody: RebillSaveBody = {
      invoice_group_id: invoiceGroupDetails?.id,
      original_invoice_number: originalInvoice?.invoice_number,
      rebill_transactions: transactionsToSave as InvoiceTransaction[],
    }

    try {
      // Send to backend for final processing
      await saveRebillInvoice(rebillSaveBody)

      // Reset UI state after successful save
      setIsRebillMode(false)
      setRebillInvoice(null)
      setRebillInvoiceChain([])

      // Refresh data from backend
      queryClient.invalidateQueries({ queryKey: ['invoiceGroupDetails'] })
    } catch (error) {
      console.error(error)
    } finally {
      // Always hide saving indicator regardless of success/failure
      setIsSavingRebill(false)
    }
  }

  return {
    isRecalcModalVisible,
    isSavingRebill,
    rebillInvoiceChain,
    setIsRebillMode,
    isRebillMode,
    hasProcessedRebillParam,
    setHasProcessedRebillParam,
    isConfirmRebillModalVisible,
    setIsConfirmRebillModalVisible,
    rebillMetadata,
    triggerRebillMode,
    overrideRebillLineItem,
    addRebillLineItem,
    isTriggeringCR,
    handleSaveRebill,
  }
}
